import { platformFromVersions, CONNECTABLE_PLATFORMS } from './platforms.js';
import { isVideoAsset, resolvePosterUrl } from '../components/preview/normalizePost.js';

function manualCaptionText(post) {
  const caption = typeof post?.caption === 'string' ? post.caption : '';
  const hashtags = Array.isArray(post?.hashtags) ? post.hashtags.filter(tag => typeof tag === 'string' && tag.trim()).map(tag => `#${tag.trim().replace(/^#+/, '')}`) : [];
  const tags = [...new Set(hashtags)].filter(tag => !caption.includes(tag));
  return tags.length ? `${caption}\n\n${tags.join(' ')}` : caption;
}

export function canEditManualDraft(raw) {
  const item = raw?.contentItem;
  return Boolean(item?.id && item.brandProfileId && raw.activeVersion?.id
    && ['manual', 'first_post', 'preview'].includes(item.source)
    && ['draft', 'ready'].includes(item.status) && !item.archivedAt
    && !(raw.publishSummary?.targets?.length)
    && (item.generationStatus == null || item.generationStatus === 'ready')
    && !['queued', 'running', 'processing', 'generating', 'visualizing', 'pending'].includes(raw.job?.visualStatus));
}

export function manualDraftForm(raw) {
  const version = raw.activeVersion;
  const platform = platformFromVersions(version.platformVersions) || raw.contentItem.platform || 'instagram';
  const variant = version.platformVersions?.[platform];
  const caption = typeof variant === 'string' ? variant : variant?.caption || variant?.body || version.body || version.caption || '';
  const media = (raw.carouselSlides || []).map(slide => {
    const asset = slide.mediaAsset || {};
    return { id: asset.id || slide.mediaAssetId, type: isVideoAsset(asset) ? 'video' : 'image',
      url: asset.publicUrl || asset.public_url || slide.imageUrl || '', retained: true,
      posterUrl: resolvePosterUrl(asset), durationSeconds: asset.durationSeconds, mimeType: asset.mimeType };
  }).filter(asset => asset.id);
  // Never discard media that exists but lacks a stable server asset identifier.
  const hasUnresolvedMedia = (raw.carouselSlides?.length || 0) !== media.length
    || (!media.length && (raw.mediaUrls?.length || version.mediaUrls?.length || version.imageUrl || version.videoUrl || raw.mediaPreview?.url));
  return { brandProfileId: raw.contentItem.brandProfileId, caption: manualCaptionText({ caption, hashtags: variant?.hashtags || version.hashtags }),
    platform, media, expectedVersionId: version.id, hasUnresolvedMedia: Boolean(hasUnresolvedMedia) };
}

export function manualDraftPayload(form, editing = false) {
  if (!form.brandProfileId || !form.caption.trim() || form.caption.length > 10000
    || !CONNECTABLE_PLATFORMS.some(p => p.id === form.platform)
    || !Array.isArray(form.media) || form.media.length > 10
    || form.media.some(asset => !asset.id || !['image', 'video'].includes(asset.type))
    || new Set(form.media.map(asset => asset.id)).size !== form.media.length
    || (form.media.some(asset => asset.type === 'video') && form.media.length !== 1)
    || form.hasUnresolvedMedia || (editing && !form.expectedVersionId)) return null;
  return { brandProfileId: form.brandProfileId, caption: form.caption, platform: form.platform,
    mediaAssetIds: form.media.map(asset => asset.id), allowTextOnly: form.media.length === 0, hashtags: [],
    ...(editing ? { expectedVersionId: form.expectedVersionId } : {}) };
}

export function manualSchedulePayload(raw, account, scheduledAt, now = Date.now()) {
  if (!canEditManualDraft(raw) || !account?.id || account.status !== 'connected'
    || !CONNECTABLE_PLATFORMS.some(p => p.id === account.platform)) return null;
  const date = scheduledAt ? new Date(scheduledAt) : new Date(now);
  if (!Number.isFinite(date.getTime()) || (scheduledAt && date.getTime() <= now)) return null;
  return { contentItemId: raw.contentItem.id, contentVersionId: raw.activeVersion.id,
    brandProfileId: raw.contentItem.brandProfileId, preserveExistingSchedules: true,
    scheduledAt: date.toISOString(), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    mode: scheduledAt ? 'manual' : 'publish_now', targets: [{ platform: account.platform, socialAccountId: account.id }] };
}

export function manualAccountReady(account, registry) {
  const platform = registry?.find(entry => entry.platform === account?.platform);
  return account?.status === 'connected' && platform?.enabled === true && platform.supportsPublish === true
    && (account.platform !== 'telegram' || account.capabilities?.destinationType === 'channel');
}
